import { useEffect, useMemo, useRef, useState } from 'react'
import {
  useStoriesQuery,
  sortStoriesUnviewedFirst,
  isStoryViewed,
  sendEventsBatched,
  trackStoryLoadFailed,
  prewarmVideoPlayer,
  useResetStoryViewsMutation,
  type StoryFeedItem,
  type StoryTrayShownEvent,
} from '@entities/stories'
import { ENV } from '@shared/config/env'
import { StoryViewer } from './StoryViewer'
import { LockIcon } from './icons'
import styles from './StoriesTray.module.css'

/** story_tray_shown отправляется ОДИН раз за запуск приложения. */
let trayShownSent = false

/**
 * Лента кружков-превью историй: грузит ленту, сортирует непросмотренные вперёд,
 * по тапу открывает {@link StoryViewer}. Пропсов нет — данные тянет сам.
 */
export const StoriesTray = () => {
  const { data, isLoading, error } = useStoriesQuery()
  const [session, setSession] = useState<{
    stories: StoryFeedItem[]
    index: number
  } | null>(null)
  const trayRef = useRef<HTMLDivElement>(null)
  const resetViews = useResetStoryViewsMutation()

  const ordered = useMemo(
    () => sortStoriesUnviewedFirst(data?.stories ?? []),
    [data],
  )

  // Прогреваем плеер заранее, но только если в ленте есть видео: иначе зря
  // тянем скрипт тем, у кого одни картинки.
  useEffect(() => {
    const hasVideo = ordered.some((s) =>
      s.slides?.some((slide) => slide.media_type === 'video'),
    )
    if (hasVideo) prewarmVideoPlayer()
  }, [ordered])

  // Аналитика: лента не загрузилась. story_id не шлём — не приехала вся лента.
  const loadFailSentRef = useRef(false)
  useEffect(() => {
    if (!error || loadFailSentRef.current) return
    loadFailSentRef.current = true
    trackStoryLoadFailed(error)
  }, [error])

  // Аналитика: лента показана — один раз за запуск, с флагом видимости кружков.
  useEffect(() => {
    if (trayShownSent || ordered.length === 0) return
    trayShownSent = true
    requestAnimationFrame(() => {
      const vw = window.innerWidth
      const els = trayRef.current
        ? Array.from(trayRef.current.querySelectorAll<HTMLElement>(`.${styles.circle}`))
        : []
      const event: StoryTrayShownEvent = {
        name: 'story_tray_shown',
        stories: ordered.slice(0, 10).map((story, i) => {
          const el = els[i]
          let isVisible = true
          if (el) {
            const r = el.getBoundingClientRect()
            isVisible = r.left < vw && r.right > 0
          }
          return {
            story_id: story.story_id,
            position: i + 1,
            is_visible: isVisible,
            has_access: story.has_access,
            is_completed: isStoryViewed(story),
          }
        }),
      }
      sendEventsBatched([event])
    })
  }, [ordered])

  // Пока грузится, при ошибке и когда историй нет — не рендерим ничего:
  // лента просто не занимает место на главной. Об ошибке загрузки уже
  // сообщает аналитика (story_load_failed).
  if (isLoading || error || ordered.length === 0) return null

  return (
    <>
      <div className={styles.tray} role="list" ref={trayRef}>
        {ordered.map((story, index) => {
          const viewed = isStoryViewed(story)
          const locked = !story.has_access
          const photoClass = [
            styles.photo,
            (!viewed || locked) && styles.photoActive,
            locked && styles.photoLocked,
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <button
              key={story.story_id}
              type="button"
              role="listitem"
              className={styles.circle}
              onClick={() => setSession({ stories: ordered, index })}
            >
              <span className={photoClass}>
                <img
                  src={story.icon_url}
                  alt=""
                  className={styles.img}
                  loading="lazy"
                />
                {locked && (
                  <span className={styles.lock}>
                    <LockIcon />
                  </span>
                )}
              </span>
              <span className={styles.title}>{story.title}</span>
            </button>
          )
        })}
      </div>

      {/* Отладка: сброс просмотров, чтобы прогонять сценарии на новой ленте. */}
      {ENV.IS_DEV && (
        <button
          type="button"
          className={styles.devReset}
          onClick={() => resetViews.mutate()}
          disabled={resetViews.isLoading}
        >
          {resetViews.isLoading
            ? 'Сбрасываю…'
            : resetViews.isError
              ? 'Ошибка сброса, ещё раз'
              : 'Сбросить просмотры историй'}
        </button>
      )}

      {session && (
        <StoryViewer
          stories={session.stories}
          startIndex={session.index}
          onClose={() => setSession(null)}
        />
      )}
    </>
  )
}

export default StoriesTray
